import { cn } from "@/lib/utils";
import { Cloud } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import {
  ExpandableMessageCard,
  SimpleMessageCard,
  ToolError,
  isBootsmanResult,
} from "./shared";

type ListDeploymentsResult = {
  deployments?: Array<{
    id: string;
    status: "pending" | "executing" | "complete" | "cancelled" | "error";
    versionId: string;
    specId: string;
    createdAt: string | Date;
    updatedAt: string | Date;
  }>;
  errorMessage?: string;
};

interface DeploymentListProps {
  messageId: string;
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

export function DeploymentList({ part }: DeploymentListProps) {
  const isError = part.status === "error";
  const limit = part.args?.limit as number | undefined;

  // Extract deployments result if available
  const result = (() => {
    if (
      part.status === "complete" &&
      part.result &&
      isBootsmanResult(part.result) &&
      part.result.success
    ) {
      return part.result.data as ListDeploymentsResult;
    }
    return null;
  })();

  const deployments = result?.deployments;
  const hasDeployments =
    deployments && deployments.length > 0 && !result?.errorMessage;

  const getStatusText = () => {
    if (part.status === "pending") {
      return limit ? `Loading ${limit} Deploys...` : "Loading Deploys...";
    }

    if (isError) {
      return "Failed to List Deploys";
    }

    if (result?.errorMessage) {
      return result.errorMessage;
    }

    if (hasDeployments) {
      return `Listed ${deployments.length} Deployment${deployments.length !== 1 ? "s" : ""}`;
    }

    if (deployments && deployments.length === 0) {
      return "No Deploys Found";
    }

    return "Deploys Listed";
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toISOString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "complete":
        return "text-green-600";
      case "error":
      case "cancelled":
        return "text-red-600";
      case "executing":
        return "text-blue-600";
      default:
        return "text-muted-foreground";
    }
  };

  // Show simple card if no deployments or successful with minimal info
  if (!hasDeployments && !isError && !result?.errorMessage) {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={Cloud}
        title={getStatusText()}
      />
    );
  }

  // Show expandable card for deployment list or errors
  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Cloud}
      title={getStatusText()}
      accordionValue="deployment-list"
    >
      {/* Show deployments list */}
      {hasDeployments && (
        <div className="space-y-2 text-sm">
          {deployments.map((deployment) => (
            <div
              key={deployment.id}
              className="border rounded-lg p-3 space-y-2 bg-muted/30"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Cloud className="w-4 h-4 text-muted-foreground" />
                  <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">
                    {deployment.id}
                  </code>
                </div>
                <span
                  className={cn("text-sm", getStatusColor(deployment.status))}
                >
                  {deployment.status}
                </span>
              </div>

              <div className="space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    Created:
                  </span>
                  <span className="text-sm">
                    {formatDate(deployment.createdAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    Updated:
                  </span>
                  <span className="text-sm">
                    {formatDate(deployment.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show tool execution error */}
      {isError && part.result ? <ToolError result={part.result} /> : null}
    </ExpandableMessageCard>
  );
}
